import React from "react";
import { Box, ListItemButton, ListItemIcon, ListItemText, Tooltip, Badge } from "@mui/material";
import { useLocation, Link as RouterLink } from "react-router-dom";
import { SIDEBAR_RAIL_WIDTH, sidebarTheme } from "./sidebarDesign";

interface SidebarNavItemProps {
  item: {
    type?: string;
    text: string;
    icon: JSX.Element;
    path: string;
    fontSize?: string;
  };
  open: boolean;
  selected?: boolean;
  onClick?: () => void;
  badgeContent?: number;
  variant?: "default" | "landing";
}

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  item,
  open,
  selected,
  onClick,
  badgeContent,
  variant = "default",
}) => {
  const location = useLocation();
  const isActive = selected ?? location.pathname === item.path;

  const isLanding = variant === "landing";

  const activeColor = isLanding ? sidebarTheme.activeText : "var(--primary-700)";
  const idleColor = isLanding ? sidebarTheme.idleText : "var(--primary-900)";
  const hoverBg = isLanding
    ? sidebarTheme.hoverBg
    : isActive
      ? "var(--neutral-200)"
      : "var(--neutral-50)";

  // Fixed rail column (72px) + label to the right. When the shell clips to rail width,
  // only the centered icon remains visible — no leftover text, no re-centering bounce.
  return (
    <Tooltip title={!open ? item.text : ""} placement="right" arrow enterDelay={500}>
      <ListItemButton
        component={RouterLink}
        to={item.path}
        onClick={onClick}
        selected={isActive}
        aria-current={isActive ? "page" : undefined}
        sx={{
          position: "relative",
          borderRadius: 0,
          minHeight: isLanding ? 42 : 50,
          height: isLanding ? 42 : undefined,
          flexGrow: 0,
          flexShrink: 0,
          mx: 0,
          px: 0,
          py: 0,
          fontFamily: "var(--primary-font-family)",
          transition: "color 0.2s ease",
          backgroundColor: "transparent",
          color: isActive ? activeColor : idleColor,
          border: "none",
          boxShadow: "none",
          // Same size as the old pill — background only (no border / shadow).
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            bottom: 0,
            left: open ? 9 : 14,
            right: open ? 9 : "auto",
            width: open ? "auto" : SIDEBAR_RAIL_WIDTH - 28,
            borderRadius: "6px",
            bgcolor: isActive ? sidebarTheme.activeBg : "transparent",
            zIndex: 0,
            pointerEvents: "none",
          },
          "&:hover": {
            backgroundColor: "transparent",
            color: activeColor,
            transform: "none",
            boxShadow: "none",
            "&::before": {
              // Keep selected bg when already active; only idle items get hoverBg.
              bgcolor: isActive ? sidebarTheme.activeBg : hoverBg,
            },
            "& .MuiListItemIcon-root": {
              color: activeColor,
            },
          },
          "&.Mui-selected": {
            boxShadow: "none",
            backgroundColor: "transparent",
          },
          "&.Mui-selected:hover": {
            backgroundColor: "transparent",
            "&::before": {
              bgcolor: sidebarTheme.activeBgHover,
            },
          },
          "& > *": {
            position: "relative",
            zIndex: 1,
          },
        }}
      >
        <Box
          sx={{
            width: SIDEBAR_RAIL_WIDTH,
            minWidth: SIDEBAR_RAIL_WIDTH,
            height: isLanding ? 42 : 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              m: 0,
              justifyContent: "center",
              color: isActive ? activeColor : idleColor,
              fontSize: "1.1rem",
            }}
          >
            {badgeContent && badgeContent > 0 ? (
              <Badge color="error" badgeContent={badgeContent} max={99} overlap="circular">
                {item.icon}
              </Badge>
            ) : (
              item.icon
            )}
          </ListItemIcon>
        </Box>

        {open ? (
          <ListItemText
            primary={item.text}
            sx={{
              flex: 1,
              minWidth: 0,
              pr: 2,
              m: 0,
              "& .MuiListItemText-primary": {
                fontWeight: 500,
                fontSize: "1rem",
                fontFamily: "var(--primary-font-family)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "inherit",
              },
            }}
          />
        ) : null}
      </ListItemButton>
    </Tooltip>
  );
};

export default SidebarNavItem;
